// no template — prototype components (s6-kicker, z-button, ui-pill); the guide has no screen in approved-workspace.html
import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react'
import { ProtoButton } from '@/features/d0/states'
import { CHAPTERS, isAvailable, placementOf } from './registry'
import { EMPTY_STATE_COPY, FIRST_RUN_COPY, RESTRICTED_COPY, RETURNING_COPY } from './copy'
import { chapterRecord, currentStep, isInfo, isPracticed, type TutorialEvent, type TutorialState } from './machine'

export interface StepCardProps {
  state: TutorialState
  /** Events that enter a step need the bus sequence; the host stamps `at`. */
  send: (e: DistributiveOmit<TutorialEvent, 'at'>) => void
  onOpenGuide: () => void
  /** Desktop only: the card moves to the left edge when it would cover the target. */
  side: 'right' | 'left'
  mobile: boolean
}

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never

// The guide's one card: what this step teaches, what to do, and how to leave it. Never modal.
export default function StepCard({ state, send, onOpenGuide, side, mobile }: StepCardProps) {
  const step = currentStep(state)
  const chapter = CHAPTERS.find((c) => c.id === step.chapter)!
  const n = chapter.stepIds.indexOf(step.id) + 1
  const m = chapter.stepIds.length
  const headingRef = useRef<HTMLHeadingElement>(null)
  const focusKey = state.mode === 'waiting' ? null : `${step.id}:${state.mode}:${state.pauseReason ?? ''}`

  useEffect(() => {
    if (focusKey) headingRef.current?.focus({ preventScroll: true })
  }, [focusKey])

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape' || e.defaultPrevented) return
    e.preventDefault()
    send({ type: 'PAUSE' })
  }

  const branch = placementOf(step, state.ctx).branch
  const restricted = !isAvailable(step, state.caps)
  const practiced = isPracticed(state, step)
  const info = isInfo(state, step)
  const status = chapterRecord(state, step.chapter).status
  const back = state.index > 0 && <ProtoButton className="z-quiet" onClick={() => send({ type: 'BACK' })}>Back</ProtoButton>
  const exit = <ProtoButton className="z-quiet" onClick={() => send({ type: 'EXIT' })}>Exit guide</ProtoButton>

  let label = `Guide step: ${step.title}. Step ${n} of ${m}`
  let heading = step.title
  let body: ReactNode = null
  let actions: ReactNode = null

  if (state.mode === 'chapterDone') {
    const done = status === 'completed'
    label = `Guide: ${chapter.title} ${done ? 'completed' : 'skipped'}`
    heading = chapter.title
    body = <p className="t0-card__text"><span className="ui-pill">{done ? 'Completed' : 'Skipped'}</span> {done ? 'You went through every step in this chapter.' : 'Some steps were skipped. You can replay this chapter from the guide at any time.'}</p>
    actions = <>{<ProtoButton className="z-primary" onClick={() => send({ type: 'NEXT' })}>{step.chapter === 'T18' ? 'Finish' : 'Next chapter'}</ProtoButton>}{back}{exit}</>
  } else if (state.mode === 'finished') {
    label = 'Guide finished'
    heading = 'You finished the guide'
    body = <p className="t0-card__text">Every chapter is in your summary. Open the guide to replay any chapter.</p>
    actions = <><ProtoButton className="z-primary" onClick={onOpenGuide}>Open summary</ProtoButton>{exit}</>
  } else if (state.mode === 'paused') {
    const blocked = state.pauseReason !== 'user'
    body = <p className="t0-card__text">{blocked ? step.recovery : 'The guide is paused. Nothing on the page was changed.'}</p>
    actions = blocked
      ? <><ProtoButton className="z-primary" onClick={() => send({ type: 'RESUME' })}>Retry</ProtoButton><ProtoButton onClick={() => send({ type: 'SKIP_STEP' })}>Skip this step</ProtoButton>{exit}</>
      : <><ProtoButton className="z-primary" onClick={() => send({ type: 'RESUME' })}>Resume</ProtoButton>{exit}</>
  } else if (restricted) {
    heading = RESTRICTED_COPY
    body = <><p className="s6-kicker t0-card__eyebrow">{step.title}</p><p className="t0-card__text">{step.body}</p></>
    actions = <><ProtoButton className="z-primary" onClick={() => send({ type: 'CONTINUE_READONLY' })}>Continue</ProtoButton>{back}{exit}</>
  } else {
    const extra = branch === 'returning' ? RETURNING_COPY[step.id] : branch === 'firstRun' ? FIRST_RUN_COPY[step.id] : undefined
    body = (
      <>
        <p className="t0-card__text">{state.empty ? EMPTY_STATE_COPY[step.chapter] : step.body}</p>
        {extra && !state.empty && <p className="t0-card__text t0-card__soft">{extra}</p>}
        {step.subanchorCopy.length > 0 && !state.empty && (
          <ul className="t0-card__list">{step.subanchorCopy.map((s) => <li key={s.id}>{s.body}</li>)}</ul>
        )}
        {state.mode === 'waiting' && <p className="t0-card__soft" role="status">Opening this part of the page…</p>}
        {state.mode === 'showing' && !info && !practiced && <p className="t0-card__do"><strong>{step.action}</strong></p>}
        {state.mode === 'showing' && !info && practiced && <p className="t0-card__text"><span className="ui-pill">Done</span> Saved. Press Next when you are ready.</p>}
        {state.mode === 'showing' && !info && !practiced && <p className="t0-card__soft">{step.readOnly}</p>}
      </>
    )
    const primary = state.mode !== 'showing' ? null
      : info || practiced ? <ProtoButton className="z-primary" onClick={() => send({ type: 'NEXT' })}>Next</ProtoButton>
      : <ProtoButton onClick={() => send({ type: 'CONTINUE_READONLY' })}>Continue read-only</ProtoButton>
    actions = <>{primary}{back}<ProtoButton className="z-quiet" onClick={() => send({ type: 'PAUSE' })}>Pause</ProtoButton>{exit}</>
  }

  return (
    <div
      className={`t0-card ${mobile ? 't0-card--bottom' : `t0-card--${side}`}`}
      role="dialog"
      aria-modal="false"
      aria-label={label}
      onKeyDown={onKeyDown}
      data-t0-card=""
      data-t0-step={step.id}
      data-t0-mode={state.mode}
      data-t0-empty={state.empty ? '' : undefined}
    >
      <p className="s6-kicker t0-card__eyebrow">{chapter.title} · Step {n} of {m}</p>
      <h2 className="t0-card__title" ref={headingRef} tabIndex={-1}>{heading}</h2>
      {body}
      <div className="t0-card__actions">{actions}</div>
    </div>
  )
}
